type ProfileNftAsset = {
	metadata: {
		image?: string;
		image_url?: string;
		imageGatewayUrl?: string;
		imageIPFSHash?: string;
	};
	ipfsData?: {
		image?: {
			ipfsHash?: string;
			gatewayUrl?: string;
			originalUrl?: string;
		};
	} | null;
};

const RAW_IPFS_CID = /^(?:Qm[1-9A-HJ-NP-Za-km-z]{44}|b[a-z2-7]{20,})$/;

/**
 * Our own Kubo gateways, from VITE_WATCHER_IPFS_GATEWAY_URLS.
 *
 * WHY: we run two Kubo gateways and pin our own content. Resolving member NFT
 * images through ipfs.io/dweb.link means a third party decides whether a paying
 * member sees their own NFT, under a rate limit we do not control. The owned set
 * leads; the public gateways remain only as a fallback for a deployment whose
 * env is absent, so a missing variable degrades instead of blanking images.
 *
 * Fail-closed parse: exactly two distinct https origins with no credentials,
 * path, query, or fragment — the same contract as Watcher-HQ's ipfs-gateway.ts.
 */
function ownedIpfsGatewayOrigins(): string[] {
	try {
		const parsed: unknown = JSON.parse(
			import.meta.env.VITE_WATCHER_IPFS_GATEWAY_URLS || '[]'
		);
		if (!Array.isArray(parsed) || parsed.length !== 2) return [];
		const origins = parsed.map((value) => {
			if (typeof value !== 'string') throw new Error('IPFS_GATEWAY_INVALID');
			const url = new URL(value);
			if (
				url.protocol !== 'https:' ||
				url.username ||
				url.password ||
				url.pathname !== '/' ||
				url.search ||
				url.hash
			) {
				throw new Error('IPFS_GATEWAY_INVALID');
			}
			return url.origin;
		});
		return new Set(origins).size === 2 ? origins : [];
	} catch {
		return [];
	}
}

/**
 * Public gateways, used ONLY when the owned set is unconfigured.
 * no-public-ipfs-fallback: reviewed last resort, reachable only without the
 * owned set, which the production build refuses to allow.
 */
const PUBLIC_IPFS_GATEWAYS = [
	'https://ipfs.io/ipfs/',
	'https://dweb.link/ipfs/',
	'https://cloudflare-ipfs.com/ipfs/',
	'https://gateway.pinata.cloud/ipfs/',
] as const;

/**
 * Ordered gateway prefixes. Our own gateways first and, when they are
 * configured, on their own.
 *
 * WHY public gateways are NOT appended after our own: we run ipfs-a and ipfs-b
 * and we pin this content. If both nodes are unavailable that is an
 * infrastructure failure we need to see; failing over to a third party would
 * hide it while handing them control over whether a paying member sees their
 * own NFT. The production build refuses to ship without the owned set.
 */
function ipfsGatewayPrefixes(): string[] {
	const owned = ownedIpfsGatewayOrigins().map((origin) => `${origin}/ipfs/`);
	if (owned.length > 0) return owned;
		// no-public-ipfs-fallback: only reached when the owned gateway set is unconfigured
	return [...PUBLIC_IPFS_GATEWAYS];
}

function validatedIpfsUri(value?: string | null) {
	const cid = value?.trim() ?? '';
	return RAW_IPFS_CID.test(cid) ? `ipfs://${cid}` : '';
}

function normalizeNftImageSrc(value?: string | null) {
	const src = typeof value === 'string' ? value.trim() : '';
	if (!src) return '';
	if (src.startsWith('ipfs://')) {
		const cidPath = src
			.replace(/^ipfs:\/\//i, '')
			.replace(/^ipfs\//i, '')
			.replace(/[?#].*$/, '');
		if (!cidPath) return '';
		// Owned gateway first; the public ones stay as ordered fallbacks.
		return `${ipfsGatewayPrefixes()[0]}${cidPath}`;
	}
	if (/^https?:\/\//i.test(src)) return src;
	if (src.startsWith('data:image/')) return src;
	if (src.startsWith('/')) return src;
	return '';
}

export function ipfsGatewayCandidates(value: string) {
	const trimmed = value.trim();
	const isLegacyThirdwebGateway = /^https?:\/\/[^/]+(?:\.ipfscdn\.io|\.thirdweb\.com)(?:\/|$)/i.test(
		trimmed
	);
	const ipfsMatch =
		trimmed.match(/^ipfs:\/\/(.+)$/i) ||
		trimmed.match(/\/ipfs\/([^?#]+)/i);
	const cidPath = ipfsMatch?.[1]
		?.replace(/^ipfs\//i, '')
		.replace(/[?#].*$/, '');
	if (!cidPath) return isLegacyThirdwebGateway ? [] : [trimmed];
	return [
		...ipfsGatewayPrefixes().map((prefix) => `${prefix}${cidPath}`),
		isLegacyThirdwebGateway ? '' : trimmed,
	].filter(Boolean);
}

function uniqueImageCandidates(
	...values: Array<string | undefined | null>
) {
	const seen = new Set<string>();
	return values
		.flatMap((value) => (value ? ipfsGatewayCandidates(value) : []))
		.map(normalizeNftImageSrc)
		.filter((value): value is string => Boolean(value))
		.filter((value) => {
			const key = value.toLowerCase();
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
}

export function getGameIdImageCandidates(
	nft: ProfileNftAsset,
	fallback: string
) {
	return uniqueImageCandidates(
		validatedIpfsUri(nft.metadata.imageIPFSHash),
		validatedIpfsUri(nft.ipfsData?.image?.ipfsHash),
		nft.metadata.image,
		nft.metadata.image_url,
		nft.metadata.imageGatewayUrl,
		nft.ipfsData?.image?.gatewayUrl,
		nft.ipfsData?.image?.originalUrl,
		fallback
	);
}

export function getAccessPassImageCandidates(
	nft: ProfileNftAsset,
	fallback: string
) {
	return uniqueImageCandidates(
		validatedIpfsUri(nft.metadata.imageIPFSHash),
		validatedIpfsUri(nft.ipfsData?.image?.ipfsHash),
		nft.metadata.imageGatewayUrl,
		nft.metadata.image_url,
		nft.metadata.image,
		nft.ipfsData?.image?.gatewayUrl,
		nft.ipfsData?.image?.originalUrl,
		fallback
	);
}
